import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import {
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SendIcon from '@mui/icons-material/Send';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  TypingIndicator,
  MessageModel,
} from '@chatscope/chat-ui-kit-react';
import { useAuthClient, useOrganizationsApi } from 'app/shared/hooks';

import { CustomTypography } from 'app/shared/components/generic-ui/Typography/Typography';
import { useStyles } from './Chat.styles';

interface IChatResponse {
  data?: string;
  message?: string;
  content?: string;
  response?: string;
  answer?: string;
}

const getResponseMessage = (response: IChatResponse | string) => {
  if (typeof response === 'string') {
    return response;
  }

  return (
    response.data ||
    response.message ||
    response.content ||
    response.response ||
    response.answer
  );
};

const ChatMessage = ({
  message,
  markdownClass,
}: {
  message: MessageModel;
  markdownClass: string;
}) => {
  if (message.direction !== 'incoming') {
    return <span>{message.message}</span>;
  }

  return (
    <div className={markdownClass}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {message.message || ''}
      </ReactMarkdown>
    </div>
  );
};

const Chat = () => {
  const { organizationId }: { organizationId: string } = useRouteParams();
  const { classes } = useStyles();
  const { fetchOrganization, organizationDetails } = useOrganizationsApi({
    organizationId,
  });
  const [messages, setMessages] = useState<MessageModel[]>([
    {
      message: 'Hello! How can I help you?',
      sender: 'Assistant',
      direction: 'incoming',
      position: 'first',
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const messagesRef = useRef(messages);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const voiceTextRef = useRef('');
  const authClient = useAuthClient();

  const scrollMessagesToBottom = () => {
    const messageList = chatContainerRef.current?.querySelector(
      '.cs-message-list__scroll-wrapper',
    ) as HTMLElement | null;

    if (messageList) {
      messageList.scrollTop = messageList.scrollHeight;
    }
  };

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  useLayoutEffect(() => {
    const container = chatContainerRef.current;
    const scrollWrapper = container?.querySelector(
      '.cs-message-list__scroll-wrapper',
    );

    if (!scrollWrapper) {
      return;
    }

    const scrollAfterRender = () => {
      scrollMessagesToBottom();
      window.requestAnimationFrame(() => {
        scrollMessagesToBottom();
        window.setTimeout(scrollMessagesToBottom, 50);
      });
    };

    const frame = window.requestAnimationFrame(scrollAfterRender);
    const timeout = window.setTimeout(scrollAfterRender, 200);
    const observer = new MutationObserver(scrollAfterRender);
    observer.observe(scrollWrapper, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    const resizeObserver = new ResizeObserver(scrollAfterRender);
    resizeObserver.observe(scrollWrapper);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [messages, isTyping]);

  const updateMessages = (nextMessages: MessageModel[]) => {
    messagesRef.current = nextMessages;
    setMessages(nextMessages);
  };

  const handleSendRequest = async (message: string) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return;
    }

    setMessageInput('');
    const newMessage = {
      message: trimmedMessage,
      direction: 'outgoing',
      sender: 'You',
      position: 'normal',
    } as const;

    updateMessages([...messagesRef.current, newMessage]);
    setIsTyping(true);

    try {
      const response = await authClient(
        `/api/v1/${organizationId}/MicrosoftFoundry/message`,
        {
          method: 'POST',
          data: { message: trimmedMessage },
        },
      );
      const content = getResponseMessage(response as IChatResponse | string);
      if (content) {
        const chatResponse = {
          message: content,
          sender: 'Assistant',
          position: 'normal',
          direction: 'incoming',
        } as const;
        updateMessages([...messagesRef.current, chatResponse]);
      }
    } catch (error) {
      updateMessages([
        ...messagesRef.current,
        {
          message: 'Sorry, I could not process that message. Please try again.',
          sender: 'Assistant',
          position: 'normal',
          direction: 'incoming',
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const toggleVoiceInput = async () => {
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setVoiceError('Voice input is not supported by this browser.');
      return;
    }

    if (!window.isSecureContext) {
      setVoiceError('Voice input requires HTTPS or localhost.');
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceError('This browser does not provide microphone access.');
      return;
    }

    let microphoneStream: MediaStream | undefined;
    try {
      microphoneStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      microphoneStream.getTracks().forEach(track => track.stop());
    } catch (error) {
      const permissionError = error as DOMException;
      const errorMessage =
        permissionError.name === 'NotAllowedError'
          ? 'Microphone permission was denied. Allow microphone access in the browser address bar and try again.'
          : permissionError.name === 'NotFoundError'
          ? 'No microphone was found.'
          : 'Unable to access the microphone.';
      setVoiceError(errorMessage);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;
    let hasSpeechResult = false;
    recognition.onstart = () => {
      setVoiceError('');
      setIsListening(true);
      voiceTextRef.current = messageInput.trim();
    };
    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (
        let index = event.resultIndex;
        index < event.results.length;
        index++
      ) {
        const result = event.results[index];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      if (finalTranscript.trim() || interimTranscript.trim()) {
        hasSpeechResult = true;
        if (finalTranscript.trim()) {
          voiceTextRef.current =
            `${voiceTextRef.current} ${finalTranscript}`.trim();
        }
        setMessageInput(`${voiceTextRef.current} ${interimTranscript}`.trim());
      }
    };
    recognition.onerror = (event: any) => {
      const errorMessage =
        event.error === 'not-allowed'
          ? 'Microphone permission was denied. Allow microphone access in the browser address bar and try again.'
          : event.error === 'no-speech'
          ? 'No speech was detected. Speak clearly near the microphone and try again.'
          : event.error === 'audio-capture'
          ? 'No microphone was found or it is already in use.'
          : `Voice input failed: ${event.error || 'unknown error'}.`;
      setVoiceError(errorMessage);
      setIsListening(false);
    };
    recognition.onend = () => {
      setIsListening(false);
      if (!hasSpeechResult) {
        setVoiceError(
          'No speech was detected. Speak clearly near the microphone and try again.',
        );
      }
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (error) {
      setVoiceError('Voice input could not be started. Try again.');
      setIsListening(false);
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendRequest(messageInput);
    }
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <CustomTypography variant="header3" bold color="accentDark">
          {`${organizationDetails?.supplierName || ''} AI Chat`}
        </CustomTypography>
      </div>
      <div ref={chatContainerRef} className={classes.chatContainer}>
        <MainContainer>
          <ChatContainer>
            <MessageList
              scrollBehavior="smooth"
              autoScrollToBottom
              autoScrollToBottomOnMount
              typingIndicator={
                isTyping ? (
                  <TypingIndicator content="Assistant is typing" />
                ) : null
              }
            >
              {messages.map((message, i) => {
                return (
                  <Message key={i} model={message}>
                    {message.direction === 'incoming' ? (
                      <Message.CustomContent>
                        <ChatMessage
                          message={message}
                          markdownClass={classes.markdown}
                        />
                      </Message.CustomContent>
                    ) : (
                      <Message.TextContent text={message.message || ''} />
                    )}
                  </Message>
                );
              })}
            </MessageList>
          </ChatContainer>
        </MainContainer>
        <div className={classes.messageInputWrapper}>
          <TextField
            className={classes.messageField}
            variant="outlined"
            placeholder="Type a message"
            value={messageInput}
            onChange={event => setMessageInput(event.target.value)}
            onKeyDown={handleInputKeyDown}
            multiline
            maxRows={4}
            slotProps={{
              htmlInput: { 'aria-label': 'Message' },
            }}
          />
          <div className={classes.actionButtons}>
            <IconButton
              aria-label={isListening ? 'Stop voice input' : 'Use voice input'}
              onClick={toggleVoiceInput}
              color={isListening ? 'secondary' : 'default'}
              size="large"
            >
              {isListening ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
            <IconButton
              aria-label="Send message"
              onClick={() => handleSendRequest(messageInput)}
              disabled={!messageInput.trim() || isTyping}
              color="primary"
              size="large"
            >
              <SendIcon />
            </IconButton>
          </div>
        </div>
        {voiceError && <div className={classes.voiceError}>{voiceError}</div>}
      </div>
    </div>
  );
};

export default Chat;
